import { ATTR_NAMES, getAttrFromProductByName } from '../../types/attrNames';
import { CmsProduct } from '../../types/cmsProduct';
import { formatAttributeItemValue } from '../../utils/helpers/attrHelpers';
import { currencyFormatterFactory } from '../../utils/number';

/**
 * One product's result from the `calculatePersonalLoans` batch (CMS-1921).
 * Every field arrives as a STRING: the calculation service formats server-side
 * (`formatRoundedAmount` / `formatPercentage`), so it has already rounded and
 * there is no more precision behind these values.
 *
 * `interest` is the rate the calculation actually used. It is deliberately what
 * ANNUAL_RATE reads, NOT the sibling `annualRate` the query also returns:
 * the service picks a flat-rate interest model precisely when the rate band's
 * `annualRate` is 0, so `annualRate` reads "0" for exactly the flat-rate
 * products ANNUAL_RATE is meant to describe. `RecalculateProductAttributes`
 * has always read `interest.interestRate` for the same reason.
 */
export type PersonalLoanCalculation = {
  monthlyRepayment?: string;
  totalFees?: string;
  totalInterest?: string;
  totalRepayment?: string;
  annualisedPercentageRate?: string;
  dailyInterest?: string;
  introductoryAnnualRate?: string;
  introductoryRateOnPrincipalReducing?: string;
  interest?: { interestRate?: string };
};

/**
 * ONE personal-loan attribute's value for one product.
 *
 * - `raw` — the number behind it, for a consumer with its own presentation.
 * - `formatted` — ready to render: the attribute's own Strapi `prefix`/`suffix`
 *   applied over the number, so units stay editor-owned and a value looks the
 *   same here as in the live comparison table.
 */
export type PersonalLoanCalculatedValue = {
  raw: number;
  formatted: string;
};

/**
 * How a value is presented before the attribute's prefix/suffix go around it,
 * mirroring `RecalculateProductAttributes`:
 * - `amount` — whole currency units with thousands separators ("8,072");
 * - `rate` — the digits exactly as the service produced them, with the unit
 *   left to the attribute's suffix ("1.30" + "%").
 */
type Presentation = 'amount' | 'rate';

type FieldSpec = {
  select: (calculation: PersonalLoanCalculation) => string | undefined;
  presentation: Presentation;
  /**
   * Whether a zero from the service means "does not apply" rather than a real
   * zero. True only for the introductory-tier attributes, which the ticket
   * scopes to "products with an introductory tier only" (CMS-1921 R2): the
   * service has no way to say "absent", so a product without such a tier comes
   * back as 0 and would otherwise render as a genuine 0% rate.
   *
   * Not a blanket rule — a zero handling fee is a real value and must show.
   */
  zeroMeansAbsent?: true;
};

/**
 * The personal-loan attributes and the calculation field behind each. This is
 * the ONE list: `registry.ts` maps exactly these names to the PL_CALCULATOR
 * provider, and the `Record` is total, so adding a name without saying which
 * field feeds it does not compile.
 */
export const PERSONAL_LOAN_ATTRIBUTES = [
  ATTR_NAMES.ANNUAL_RATE,
  ATTR_NAMES.APR,
  ATTR_NAMES.MONTHLY_REPAYMENT,
  ATTR_NAMES.HANDLING_FEE,
  ATTR_NAMES.TOTAL_INTEREST,
  ATTR_NAMES.TOTAL_REPAYMENT,
  ATTR_NAMES.INTRODUCTORY_ANNUAL_RATE,
  ATTR_NAMES.INTRODUCTORY_MONTHLY_REPAYMENT,
  ATTR_NAMES.DAILY_INTEREST,
] as const;

export type PersonalLoanAttribute = (typeof PERSONAL_LOAN_ATTRIBUTES)[number];

const FIELD_BY_ATTRIBUTE: Record<PersonalLoanAttribute, FieldSpec> = {
  [ATTR_NAMES.ANNUAL_RATE]: {
    select: (calculation) => calculation.interest?.interestRate,
    presentation: 'rate',
  },
  [ATTR_NAMES.APR]: {
    select: (calculation) => calculation.annualisedPercentageRate,
    presentation: 'rate',
  },
  [ATTR_NAMES.MONTHLY_REPAYMENT]: {
    select: (calculation) => calculation.monthlyRepayment,
    presentation: 'amount',
  },
  [ATTR_NAMES.HANDLING_FEE]: {
    select: (calculation) => calculation.totalFees,
    presentation: 'amount',
  },
  [ATTR_NAMES.TOTAL_INTEREST]: {
    select: (calculation) => calculation.totalInterest,
    presentation: 'amount',
  },
  [ATTR_NAMES.TOTAL_REPAYMENT]: {
    select: (calculation) => calculation.totalRepayment,
    presentation: 'amount',
  },
  [ATTR_NAMES.INTRODUCTORY_ANNUAL_RATE]: {
    select: (calculation) => calculation.introductoryAnnualRate,
    presentation: 'rate',
    zeroMeansAbsent: true,
  },
  [ATTR_NAMES.INTRODUCTORY_MONTHLY_REPAYMENT]: {
    select: (calculation) => calculation.introductoryRateOnPrincipalReducing,
    presentation: 'amount',
    zeroMeansAbsent: true,
  },
  [ATTR_NAMES.DAILY_INTEREST]: {
    select: (calculation) => calculation.dailyInterest,
    presentation: 'rate',
    // A zero daily interest next to a non-zero APR reads as a broken cell
    // rather than as a rate of zero (Kimchi Yim's call, CMS-1921 QA round 2).
    zeroMeansAbsent: true,
  },
};

const isPersonalLoanAttribute = (
  name: string | undefined,
): name is PersonalLoanAttribute => !!name && name in FIELD_BY_ATTRIBUTE;

/**
 * The service returns "673.00" and "0.90%". Strip everything that is not part
 * of the number; the unit is re-applied from the attribute's own configuration.
 */
const numericText = (value: string): string => value.replace(/[^0-9.-]/g, '');

const parseServiceNumber = (value: string): number | undefined => {
  const parsed = Number(numericText(value));
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * A rate keeps the service's own digits instead of being printed back from the
 * parsed number: `Number("1.30")` is 1.3, so re-deriving the text would render
 * "1.3 %" where the recalculated attribute — and the comparison table reading
 * it — shows "1.30 %". The service has already rounded, so its text IS the
 * presentation. An amount is different: it is re-formatted to whole units with
 * thousands separators, which the service does not do.
 */
const presentNumber = (
  serviceValue: string,
  raw: number,
  presentation: Presentation,
): string =>
  presentation === 'amount'
    ? currencyFormatterFactory({
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(raw)
    : numericText(serviceValue);

/**
 * The PL provider's resolution: pick this attribute's field out of the
 * product's batch result and dress it in the attribute's own formatting.
 *
 * `undefined` when the attribute is not a personal-loan one, when the batch has
 * no result for this product (no matching rate band, or it was not part of the
 * request), or when the field is absent — the consumer decides what an
 * unresolved cell shows.
 */
export const resolvePersonalLoanValue = (
  attributeName: string | undefined,
  product: CmsProduct | undefined,
  calculationsByProductId: Record<string, PersonalLoanCalculation> | undefined,
): PersonalLoanCalculatedValue | undefined => {
  if (!isPersonalLoanAttribute(attributeName)) return undefined;

  const productId = product?.title;
  if (!productId) return undefined;

  const calculation = calculationsByProductId?.[productId];
  if (!calculation) return undefined;

  const { select, presentation, zeroMeansAbsent } =
    FIELD_BY_ATTRIBUTE[attributeName];
  const serviceValue = select(calculation);
  if (!serviceValue) return undefined;

  const raw = parseServiceNumber(serviceValue);
  if (raw === undefined) return undefined;
  if (zeroMeansAbsent && raw === 0) return undefined;

  // The attribute carries the unit (a "%" suffix, a currency prefix) and is
  // authored per market, so the number is dressed by its own configuration
  // rather than by anything hardcoded here. Every personal-loan attribute is a
  // number attribute; a product missing it — or carrying some other shape —
  // still renders the bare number instead of nothing.
  const attribute = getAttrFromProductByName(attributeName, product);
  const presented = presentNumber(serviceValue, raw, presentation);
  const formatted =
    attribute?.__typename === 'ComponentAttributesAttrNumber'
      ? formatAttributeItemValue({ ...attribute, value: presented })
      : presented;

  return { raw, formatted };
};
